import { onBeforeUnmount, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

interface HashScrollOptions {
  offset?: number
}

const STICKY_SELECTOR = '#header-wrapper'
const HEADER_GAP = 8

/**
 * Scrolls to same-page anchors even when their target still sits inside a
 * LazyComponent that has not rendered yet — on click, on deep links and on
 * back/forward navigation.
 */
export const useHashScroll = ({ offset }: HashScrollOptions = {}) => {
  const route = useRoute()
  const router = useRouter()

  const scrollToHash = async (hash: string) => {
    const id = _getId(hash)
    if (!id) return

    const { useLazyStore } = await import('@/stores/lazyStore')
    // Attribute selector, ids from the CMS are not always valid css identifiers
    void useLazyStore().scroll({
      selector: `[id="${id.replace(/"/g, '\\"')}"]`,
      offset: offset ?? _stickyHeaderHeight()
    })
  }

  /**
   * Older CMS anchors carry an inline onclick that scrolls natively and returns
   * false. It runs before our own handler and cancels the event, so every click
   * with the target already rendered would end up behind the sticky header.
   * The href alone does everything that handler was there for.
   */
  const onClickCapture = (event: MouseEvent) => {
    _getSamePageLink(event)?.removeAttribute('onclick')
  }

  const onClick = (event: MouseEvent) => {
    const hash = _getSamePageLink(event)?.hash
    if (!hash) return

    event.preventDefault()

    if (route?.hash !== hash) {
      void router?.push({ path: route.path, query: route.query, hash })
    }

    void scrollToHash(hash)
  }

  const onHashChange = () => {
    void scrollToHash(window.location.hash)
  }

  onMounted(() => {
    document.addEventListener('click', onClickCapture, true)
    document.addEventListener('click', onClick)
    window.addEventListener('hashchange', onHashChange)

    if (window.location.hash) onHashChange()
  })

  onBeforeUnmount(() => {
    document.removeEventListener('click', onClickCapture, true)
    document.removeEventListener('click', onClick)
    window.removeEventListener('hashchange', onHashChange)
  })

  return { scrollToHash }
}

/**
 * The header only sticks from md upwards, so the anchor would end up behind it
 * on desktop without keeping its height free.
 */
const _stickyHeaderHeight = () => {
  const header = document.querySelector(STICKY_SELECTOR)
  if (!header) return 0

  const { position } = window.getComputedStyle(header)
  if (position !== 'sticky' && position !== 'fixed') return 0

  return Math.round(header.getBoundingClientRect().height) + HEADER_GAP
}

const _getId = (hash: string) => {
  const id = hash?.replace(/^#/, '') ?? ''
  return id === '' || id === 'top' ? null : id
}

/**
 * Returns the link of a plain left click pointing at an anchor of the current
 * page, null for anything we must leave to the browser or the router.
 */
const _getSamePageLink = (event: MouseEvent) => {
  if (event.defaultPrevented) return null
  if (event.button !== 0) return null
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return null
  }

  const link = (event.target as Element)?.closest?.('a[href]') as
    | HTMLAnchorElement
    | undefined
  if (!link || (link.target && link.target !== '_self')) return null

  const url = new URL(link.href, window.location.href)
  if (!_getId(url.hash)) return null
  if (url.origin !== window.location.origin) return null
  if (url.pathname !== window.location.pathname) return null
  if (url.search !== window.location.search) return null

  return link
}
